import {ChangeDetectorRef, Component, Input, OnInit, Optional} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {Translatable, TranslationService} from '@ngmedax/translation';

import {QuestionnaireEditorService} from '../../../services/questionnaire-editor.service';
import {QuestionnaireStateService} from '../../../services/questionnaire-state.service';
import {QuestionnaireVariablesService} from '../../../services/questionnaire-variables.service';
import {VariablesModalComponent} from '../variables/modal/variables-modal.component';
import {ScoringModalComponent} from '../scoring/modal/scoring-modal.component';
import {TRANSLATION_EDITOR_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';

interface MultiValueFieldType {
  value: string;
  labelKey: string;
}

// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionTypeMultiValueComponent extends Translatable {}

@Component({
  selector: 'app-qa-question-multi-value',
  templateUrl: './question-type-multi-value.component.html',
  styleUrls: ['./question-type-multi-value.component.css', '../../../shared/reusable.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionTypeMultiValueComponent implements OnInit {
  @Input() question: Questionnaire.Container;

  public locale = 'de_DE';
  public fieldTypes: MultiValueFieldType[] = [
    {value: Questionnaire.Container.Format.TEXT, labelKey: KEYS.EDITOR.FREE_TEXT},
    {value: Questionnaire.Container.Format.NUMERIC, labelKey: KEYS.EDITOR.NUMBER},
    {value: Questionnaire.Container.Format.DATE, labelKey: KEYS.EDITOR.DATE},
    {value: Questionnaire.Container.Format.SINGLE_CHOICE, labelKey: KEYS.EDITOR.DROPDOWN}
  ];

  public expandedFieldOptions: {[index: number]: boolean} = {};

  public constructor(
    @Optional() private translationService: TranslationService,
    private questionnaireEditor: QuestionnaireEditorService,
    private questionnaireState: QuestionnaireStateService,
    private questionnaireVariables: QuestionnaireVariablesService,
    private ref: ChangeDetectorRef,
    private modal: NgbModal
  ) {}

  public ngOnInit() {
    if (!this.question.elements) {
      this.question.elements = [];
    }

    for (let i = 0; i < this.question.elements.length; i++) {
      this.applyFieldTypeDefaults(this.question.elements[i], this.question.elements[i].format || Questionnaire.Container.Format.TEXT);
      this.ensureFieldTitle(this.question.elements[i], i);
      this.expandedFieldOptions[i] = false;
    }
  }

  public onAddField(format: string = Questionnaire.Container.Format.TEXT) {
    if (!this.question.elements) {
      this.question.elements = [];
    }

    const field = this.buildFieldTemplate(format);
    this.question.elements.push(field);
    this.expandedFieldOptions[this.question.elements.length - 1] = true;
    this.updateElement();
  }

  public onDeleteField(position: number) {
    this.questionnaireEditor.confirmDelete(() => {
      const field = this.question.elements[position];
      field && field.pathHash && this.questionnaireVariables.deleteVariableMappings(field.pathHash);
      this.question.elements.splice(position, 1);

      const updated: {[index: number]: boolean} = {};
      for (const key of Object.keys(this.expandedFieldOptions)) {
        const idx = parseInt(key, 10);
        if (idx < position) {
          updated[idx] = this.expandedFieldOptions[idx];
        } else if (idx > position) {
          updated[idx - 1] = this.expandedFieldOptions[idx];
        }
      }
      this.expandedFieldOptions = updated;

      this.updateElement();
    });
  }

  public onPositionChange(currentPosition: number, newPosition: number) {
    if (!this.question.elements) {
      return;
    }
    if (currentPosition >= this.question.elements.length || newPosition < 0) {
      return;
    }
    this.question.elements.splice(newPosition, 0, this.question.elements.splice(currentPosition, 1)[0]);

    const tmp = this.expandedFieldOptions[currentPosition];
    this.expandedFieldOptions[currentPosition] = this.expandedFieldOptions[newPosition];
    this.expandedFieldOptions[newPosition] = tmp;

    this.updateElement();
  }

  public onFieldTypeChange(field: Questionnaire.Container, format: string) {
    this.applyFieldTypeDefaults(field, format);
    this.updateElement();
  }

  public onFieldLabelChange(position: number, value: string) {
    const field = this.question.elements[position];
    if (!field) {
      return;
    }
    !field.title && (field.title = {[this.locale]: ''});
    field.title[this.locale] = value || '';
    this.updateElement();
  }

  public toggleFieldOptions(position: number) {
    this.expandedFieldOptions[position] = !this.expandedFieldOptions[position];
  }

  public onVariablesModal(position: number) {
    const container = this.question.elements[position];
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = container;
    modalRef.componentInstance.disabledScopes = {upload: true};
  }

  public onScoringModal(position: number) {
    const container = this.question.elements[position];
    const modalRef = this.modal.open(ScoringModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = container;
  }

  /**
   * Toggles the mandatory flag on a multi-value field
   */
  public onToggleFieldMandatory(field: Questionnaire.Container) {
    !field.options && (field.options = {});
    field.options.mandatory = !field.options.mandatory;
    this.updateElement();
  }

  public onToggleMark(field: Questionnaire.Container) {
    !field.conditions && (field.conditions = {mark: ''} as any);

    if (!field.conditions.mark) {
      field.conditions.mark = `(container.x${field.pathHash} == true)`;
      return;
    }

    delete field.conditions.mark;
  }

  public getFieldTypeLabel(field: Questionnaire.Container): string {
    if (!field || !field.format) {
      return '';
    }
    const current = this.fieldTypes.filter((type) => type.value === field.format)[0];
    return current ? this._(current.labelKey) : field.format;
  }

  public getScoringInfo(field: Questionnaire.Container): {variableName: string, value: number} | null {
    if (!field || !field.pathHash) {
      return null;
    }

    const scope = 'scoring';
    const variableName = this.questionnaireVariables.getMappedVariableName(scope, field.pathHash);

    if (!variableName) {
      return null;
    }

    const opts = this.questionnaireVariables.getMappedVariableOptions(scope, field.pathHash, variableName);
    const value = opts && typeof opts.value === 'number' ? opts.value : 0;
    return {variableName, value};
  }

  /**
   * Updates the widthPercent option of a field or removes it when empty
   */
  public onFieldWidthChange(position: number, value: number | null) {
    const field = this.question.elements[position];
    if (!field) {
      return;
    }

    !field.options && (field.options = {});

    if (value && value >= 1 && value <= 100) {
      field.options.widthPercent = value;
    } else {
      delete field.options.widthPercent;
    }

    this.updateElement();
  }

  /**
   * Returns a warning message if the sum of all widthPercent values exceeds 100
   */
  public getWidthWarning(): string | null {
    if (!this.question.elements || !this.question.elements.length) {
      return null;
    }

    let sum = 0;
    for (const field of this.question.elements) {
      if (field.options && typeof field.options.widthPercent === 'number') {
        sum += field.options.widthPercent;
      }
    }

    return sum > 100 ? this._(KEYS.EDITOR.WIDTH_PERCENT_SUM_WARNING) : null;
  }

  /**
   * Calculates the width style for a field in the preview table
   */
  public getFieldStyle(field: Questionnaire.Container): {[key: string]: string} {
    if (!this.question.elements || !this.question.elements.length) {
      return {};
    }

    if (field.options && typeof field.options.widthPercent === 'number') {
      return {'width': field.options.widthPercent + '%'};
    }

    let usedPercent = 0;
    let fieldsWithoutWidth = 0;

    for (const f of this.question.elements) {
      if (f.options && typeof f.options.widthPercent === 'number') {
        usedPercent += f.options.widthPercent;
      } else {
        fieldsWithoutWidth++;
      }
    }

    if (fieldsWithoutWidth === 0) {
      return {};
    }

    const remaining = Math.max(0, 100 - usedPercent);
    const share = remaining / fieldsWithoutWidth;
    return {'width': share + '%'};
  }

  public updateElement() {
    this.questionnaireState.updateElement(this.question, -1);
    this.ref.markForCheck();
    this.ref.detectChanges();
  }

  private buildFieldTemplate(format: string): Questionnaire.Container {
    const field: Questionnaire.Container = {
      id: this.questionnaireEditor.generateUUID(),
      title: {[this.locale]: ''},
      format: Questionnaire.Container.Format.TEXT,
      dataType: 'string',
      displayType: Questionnaire.Container.DisplayType.TEXT
    };
    this.applyFieldTypeDefaults(field, format);
    this.ensureFieldTitle(field, this.question && this.question.elements ? this.question.elements.length : 0);
    return field;
  }

  private applyFieldTypeDefaults(field: Questionnaire.Container, format: string) {
    if (!field.options) {
      field.options = {};
    }

    const preservedWidthPercent = field.options.widthPercent;

    field.format = format;

    if (format !== Questionnaire.Container.Format.SINGLE_CHOICE) {
      field.elements = [];
    } else if (!field.elements) {
      field.elements = [];
    }

    if (format === Questionnaire.Container.Format.SINGLE_CHOICE) {
      field.dataType = 'boolean';
      field.displayType = 'dropdown';
    } else if (format === Questionnaire.Container.Format.NUMERIC) {
      field.dataType = 'number';
      field.displayType = Questionnaire.Container.DisplayType.TEXT;
      field.options.min = typeof field.options.min === 'number' ? field.options.min : 0;
      field.options.max = typeof field.options.max === 'number' ? field.options.max : 300;
      field.options.steps = typeof field.options.steps === 'number' ? field.options.steps : 1;
      field.options.disableWheelPicker = true;
      !field.options.unit && (field.options.unit = {[this.locale]: ''});
    } else if (format === Questionnaire.Container.Format.DATE) {
      field.dataType = 'string';
      field.displayType = Questionnaire.Container.DisplayType.TEXT;
      field.options.fullDateTime = !!field.options.fullDateTime;
      field.options.fullDate = true;
      field.options.monthDate = !!field.options.monthDate;
      field.options.yearDate = !!field.options.yearDate;
    } else {
      field.dataType = 'string';
      field.displayType = Questionnaire.Container.DisplayType.TEXT;
      !field.options.validation && (field.options.validation = {regex: '', mod: '', hint: {[this.locale]: ''}} as any);
    }

    if (typeof preservedWidthPercent === 'number') {
      field.options.widthPercent = preservedWidthPercent;
    }
  }

  private ensureFieldTitle(field: Questionnaire.Container, index: number) {
    !field.title && (field.title = {[this.locale]: ''});
    if (!field.title[this.locale]) {
      field.title[this.locale] = `Label ${index + 1}`;
    }
  }
}
